import React from 'react';
import { FiCheckCircle as CheckCircle, FiDownload as Download, FiUser as User, FiTarget as Target, FiChevronLeft as ChevronLeft, FiShare2 as Share } from 'react-icons/fi';
import { AssessmentResultsData } from '../hooks/useAssessment';

interface AssessmentResultsProps {
  assessmentResults: AssessmentResultsData | null;
  onDownloadPDF: () => void;
  pdfDownloading: boolean;
  onBookSME: () => void;
  onNewAssessment?: () => void;
  onBackToAssessments?: () => void;
  onShare?: () => void;
  shareLoading?: boolean;
}

const AssessmentResults: React.FC<AssessmentResultsProps> = ({
  assessmentResults,
  onDownloadPDF,
  pdfDownloading,
  onBookSME,
  onNewAssessment: _onNewAssessment,
  onBackToAssessments,
  onShare,
  shareLoading
}) => {
  if (!assessmentResults) return null;

  const formatScore = (score: number): number => {
    return Math.round(score * 100) / 100;
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-900 dark:text-yellow-200';
    return 'text-red-600 dark:text-red-400';
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'Critical': return 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300';
      case 'High': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300';
      case 'Medium': return 'bg-yellow-200/50 dark:bg-yellow-200/40 text-yellow-900 dark:text-yellow-200';
      case 'Low': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const formatCategoryName = (category: string): string => {
    const categoryNames = {
      'business_objectives': 'Business Strategy Alignment',
      'data_readiness': 'Data Foundation Maturity',
      'technical_infrastructure': 'Technical Infrastructure Readiness',
      'organizational_readiness': 'Organizational Capabilities',
      'governance_compliance': 'Governance & Compliance Readiness',
      'ai_ml_implementation': 'AI/ML Implementation Maturity',
      'ai_ml_strategy_governance': 'AI/ML Strategy & Governance',
      'data_foundation': 'Data Foundation',
      'security_compliance': 'Security & Compliance',
      'infrastructure_platform': 'Infrastructure & Platform',
      'cost_optimization_finops': 'Cost Optimization & FinOps',
      'observability_operations': 'Observability & Operations',
      'ai_model_operations': 'AI Model Operations',
      'agent_automation': 'Agent & Automation'
    };
    if (category in categoryNames) return categoryNames[category as keyof typeof categoryNames];
    // Fallback: replace underscores, title-case, then fix known acronyms
    return category
      .replaceAll('_', ' ')
      .replaceAll(/\b\w/g, (l: string) => l.toUpperCase())
      .replaceAll(/\bAi\b/g, 'AI')
      .replaceAll(/\bMl\b/g, 'ML')
      .replaceAll(/\bFinops\b/g, 'FinOps')
      .replaceAll(/\bMlops\b/g, 'MLOps')
      .replaceAll(/\bDevops\b/g, 'DevOps')
      .replaceAll(/\bIac\b/g, 'IaC')
      .replaceAll(/\bSso\b/g, 'SSO')
      .replaceAll(/\bDr\b/g, 'DR')
      .replaceAll(/\bPii\b/g, 'PII')
      .replaceAll(/\bRag\b/g, 'RAG')
      .replaceAll(/\bHitl\b/g, 'HITL');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 transition-colors">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-3">
            <div className="flex items-center space-x-4">
              {onBackToAssessments && (
                <button
                  onClick={onBackToAssessments}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex-shrink-0"
                  title="Back to Assessments"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Back to Assessments</span>
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={onDownloadPDF}
                disabled={pdfDownloading}
                className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 text-sm sm:text-base"
              >
                {pdfDownloading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span className="hidden sm:inline">Downloading...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">Download PDF Report</span>
                    <span className="sm:hidden">PDF</span>
                  </>
                )}
              </button>
              <button
                onClick={onBookSME}
                className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm sm:text-base"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Review with an SME</span>
                <span className="sm:hidden">SME</span>
              </button>
              {onShare && (
                <button
                  onClick={onShare}
                  disabled={shareLoading}
                  className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 text-sm sm:text-base"
                  title="Generate shareable link"
                >
                  <Share className="w-4 h-4" />
                  <span>Share</span>
                </button>
              )}
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white transition-colors mt-6">Assessment Results</h1>
          <p className="text-gray-600 dark:text-gray-400 transition-colors">
            Your AI strategy assessment results and recommendations
          </p>
        </div>

        {/* Overall Score */}
        {assessmentResults.overallScore != null && (
          <div className="mb-8">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6 transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white transition-colors">Overall Strategy Score</h2>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 transition-colors">Based on your responses across all categories</p>
                </div>
                <div className="text-center sm:text-right">
                  <div className={`text-3xl sm:text-4xl font-bold ${getScoreColor(assessmentResults.overallScore)}`}>
                    {formatScore(assessmentResults.overallScore)}/100
                  </div>
                  {assessmentResults.readinessLevel && (
                    <div className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getScoreColor(assessmentResults.overallScore)} transition-colors`}>
                      {assessmentResults.readinessLevel}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Category Breakdown */}
        {assessmentResults.categoryScores && typeof assessmentResults.categoryScores === 'object' && Object.keys(assessmentResults.categoryScores).length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 transition-colors">Category Breakdown</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {Object.entries(assessmentResults.categoryScores).map(([category, score]: [string, number]) => (
                <div key={category} className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6 transition-colors">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 transition-colors">
                    {formatCategoryName(category)}
                  </h3>
                  <div className="flex items-center justify-between">
                    <div className={`text-2xl font-bold ${getScoreColor(score)}`}>
                      {formatScore(score)}/100
                    </div>
                    <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2 transition-colors">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${score}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Detailed Scorecard */}
        {assessmentResults.scorecard && Array.isArray(assessmentResults.scorecard) && assessmentResults.scorecard.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 transition-colors">Detailed Scorecard</h2>
            {/* Mobile card layout */}
            <div className="md:hidden space-y-4">
              {assessmentResults.scorecard.map((row) => {
                let maturityClass: string;
                if (row.maturityLevel === 'Advanced') {
                  maturityClass = 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
                } else if (row.maturityLevel === 'Intermediate') {
                  maturityClass = 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300';
                } else if (row.maturityLevel === 'Basic') {
                  maturityClass = 'bg-yellow-200/50 dark:bg-yellow-200/40 text-yellow-900 dark:text-yellow-200';
                } else if (row.maturityLevel === 'Minimal') {
                  maturityClass = 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300';
                } else {
                  maturityClass = 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300';
                }
                return (
                  <div key={row.category} className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 transition-colors">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">{row.category}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{row.description}</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${maturityClass}`}>{row.maturityLevel}</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(row.priority)}`}>{row.priority}</span>
                    </div>
                    {row.applicableServices && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1"><strong>Services:</strong> {row.applicableServices}</p>
                    )}
                    {row.notes && (
                      <p className="text-xs text-gray-500 dark:text-gray-400"><strong>Notes:</strong> {row.notes}</p>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Desktop table layout */}
            <div className="hidden md:block bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden transition-colors">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Maturity Level
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Priority
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Applicable Services
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Notes / KPIs
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {assessmentResults.scorecard.map((row, index) => {
                      let maturityClass: string;
                      if (row.maturityLevel === 'Advanced') {
                        maturityClass = 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
                      } else if (row.maturityLevel === 'Intermediate') {
                        maturityClass = 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300';
                      } else if (row.maturityLevel === 'Basic') {
                        maturityClass = 'bg-yellow-200/50 dark:bg-yellow-200/40 text-yellow-900 dark:text-yellow-200';
                      } else if (row.maturityLevel === 'Minimal') {
                        maturityClass = 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300';
                      } else {
                        maturityClass = 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300';
                      }
                      return (
                      <tr key={row.category} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700/50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {row.category}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {row.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${maturityClass}`}>
                            {row.maturityLevel}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(row.priority)}`}>
                            {row.priority}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {row.applicableServices}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {row.notes}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Key Recommendations */}
        {assessmentResults.recommendations && Array.isArray(assessmentResults.recommendations) && assessmentResults.recommendations.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 transition-colors">Key Recommendations</h2>
            <div className="space-y-4">
              {assessmentResults.recommendations.map((rec) => (
                <div key={rec.title} className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white transition-colors">{rec.title}</h3>
                        {rec.priority && (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(rec.priority)} transition-colors`}>
                            {rec.priority} Priority
                          </span>
                        )}
                      </div>
                      {rec.description && (
                        <p className="text-gray-600 dark:text-gray-400 mb-3 transition-colors">{rec.description}</p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400 transition-colors">
                        {rec.category && <span>Category: {rec.category}</span>}
                        {rec.effort && <span>Effort: {rec.effort}</span>}
                        {rec.timeline && <span>Timeline: {rec.timeline}</span>}
                        {rec.service && <span>Service: {rec.service}</span>}
                      </div>
                    </div>
                    <div className="ml-4">
                      <Target className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Implementation Roadmap */}
        {assessmentResults.roadmap && Array.isArray(assessmentResults.roadmap) && assessmentResults.roadmap.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 transition-colors">Implementation Roadmap</h2>
            <div className="space-y-6">
              {assessmentResults.roadmap.map((phase) => (
                <div key={phase.phase} className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 transition-colors">{phase.phase}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {phase.items && Array.isArray(phase.items) && phase.items.map((item) => (
                      <div key={item} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300 transition-colors">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommended Cloud Services (AWS or Azure) */}
        {(() => {
          const cloudServices = (assessmentResults.azureServices?.length ?? 0) > 0
            ? assessmentResults.azureServices
            : assessmentResults.awsServices;
          const cloudProviderLabel = (assessmentResults.azureServices?.length ?? 0) > 0 ? 'Azure' : 'AWS';
          return cloudServices && Array.isArray(cloudServices) && cloudServices.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 transition-colors">Recommended {cloudProviderLabel} Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {cloudServices.map((service, index) => {
                let relevanceClass: string;
                if (service.relevance === 'Critical') {
                  relevanceClass = 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300';
                } else if (service.relevance === 'High') {
                  relevanceClass = 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300';
                } else if (service.relevance === 'Medium') {
                  relevanceClass = 'bg-yellow-200/50 dark:bg-yellow-200/40 text-yellow-900 dark:text-yellow-200';
                } else {
                  relevanceClass = 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
                }
                return (
                <div key={service.service || service.name || `service-${index}`} className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6 transition-colors">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white transition-colors">
                      {service.service || service.name || 'Cloud Service'}
                    </h3>
                    {service.relevance && (
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${relevanceClass} transition-colors`}>
                        {service.relevance} Relevance
                      </span>
                    )}
                  </div>
                  {service.description && (
                    <p className="text-gray-600 dark:text-gray-400 mb-2 transition-colors">{service.description}</p>
                  )}
                  {service.useCase && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">
                      <strong>Use Case:</strong> {service.useCase}
                    </p>
                  )}
                  {service.useCases && Array.isArray(service.useCases) && service.useCases.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors mb-1">
                        <strong>Use Cases:</strong>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {service.useCases.map((useCase) => (
                          <span
                            key={useCase}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                          >
                            {useCase}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
          );
        })()}

        {/* Empty state if no data available */}
        {!assessmentResults.overallScore && 
         (!assessmentResults.categoryScores || Object.keys(assessmentResults.categoryScores).length === 0) && 
         (!assessmentResults.roadmap || assessmentResults.roadmap.length === 0) && 
         (!assessmentResults.awsServices || assessmentResults.awsServices.length === 0) &&
         (!assessmentResults.azureServices || assessmentResults.azureServices.length === 0) && (
          <div className="mb-8">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center transition-colors">
              <p className="text-gray-600 dark:text-gray-400">
                Assessment results are being processed. Please check back shortly.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentResults;